import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { DfeEnrichedTransferEntry } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const LIMIT = 20;

export interface UseMintHistoryReturn {
  entries: DfeEnrichedTransferEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useMintHistory(): UseMintHistoryReturn {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.dfe.adminTransfers({ transferType: 'mint' }),
      queryFn: ({ pageParam = 0 }) =>
        api.listDfeTransfers({ transferType: 'mint', limit: LIMIT, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
    });

  const entries = data?.pages.flatMap((p) => p.transfers) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    entries,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
